// ============================================
// COST-MATH ADAPTER. Bridges legacy scope shape → cost-math.js inputs (Wave 3)
// ============================================
// Shared by analysis-pipeline, Stage4ScopeBuilder, FullQuoteEditorModal,
// project-costing. Pure mapping. No I/O, no state.
//
// Legacy scope items carry a `costBreakdown` object with nested arrays
// (materials[], labour[], equipment[], subcontractors[], otherCosts[]) and
// a string `amount` field. cost-math.js wants a flat LineItemInputs shape.

// DATA-9: parseAmount relocated to cost-math.js (canonical home); shim deleted
import { parseAmount } from './cost-math.js';
import { isRateFinalNoMarkupEnabled } from '../utils/feature-flags';
import { HBCF_DEFAULT_RATE, HBCF_THRESHOLD_INCGST, STATUTORY_INSURANCE_RE } from './cost-rules.js';
import { resolveScopePricing } from './pricing-basis.js';
import { partitionCostLines } from './cost-line-partition.js';
import {
    isHbcfEligibilityExcludedItem,
    managedScopeItemIncludesHbcf,
} from './rj-bird-pricing-methodology.js';

// Re-export parseAmount so consumers that already import from the adapter
// (boundary concerns) can reach the canonical numeric parser without also
// reaching into cost-math.js. cost-math.js stays the source of truth.
export { parseAmount };

/**
 * Normalise an AI-authored `cost_estimate` object so every numeric field
 * round-trips through parseAmount. AI output regularly contains currency
 * strings ("$1,234.50"), percentage strings ("10%"), empty strings,
 * null/undefined, and NaN — all of which break downstream arithmetic if
 * read with `|| 0` or fall through as literal strings. This is the
 * ONLY sanctioned boundary normaliser for scope-item cost reads.
 *
 * Codex WO-D / M4 (2026-04-25). Kill switch
 * `qm_disable_cost_normalize_v2` reverts callers to the legacy raw-read
 * path; see src/services/analysis-pipeline.js normalizeScopeItems.
 *
 * Returned shape intentionally mirrors the legacy cost_estimate contract
 * (direct top-level numerics + normalised nested arrays). The fields the
 * pipeline actually uses are:
 *   - directCostTotal (number)
 *   - contingencyPct  (number, defaulted by caller)
 *   - overheadPct     (number, defaulted by caller)
 *   - marginPct       (number)
 *   - materials[]     { description, qty, unit, unitRate, total }
 *   - labour[]        { trade, hours, rate, workers, total }
 *   - equipment[]     { description, total }
 *   - subcontractors[] { trade, hours, rate, qty, unit, total }
 *   - other[]         { description, total }
 *
 * Every numeric field is parseAmount-normalised. Null/undefined rows are
 * filtered. String descriptions stay strings. `unit_rate` (snake) and
 * `unitRate` (camel) are both accepted on materials for back-compat.
 *
 * Idempotent — calling it on an already-normalised object is a no-op.
 *
 * @param {Object|null|undefined} ce  raw AI cost_estimate object
 * @returns {Object} normalised cost estimate (always a plain object)
 */
export function normalizeCostEstimate(ce) {
    // why: a null ce is a valid pipeline state (the AI didn't emit
    // cost_estimate for this scope item); callers should still receive a
    // safe object so the caller's `|| 0` paths collapse to 0 cleanly.
    const src = (ce && typeof ce === 'object') ? ce : {};
    const mats = Array.isArray(src.materials) ? src.materials.filter(Boolean) : [];
    const lab  = Array.isArray(src.labour)    ? src.labour.filter(Boolean)    : [];
    const eq   = Array.isArray(src.equipment) ? src.equipment.filter(Boolean) : [];
    const sub  = Array.isArray(src.subcontractors) ? src.subcontractors.filter(Boolean) : [];
    const oth  = Array.isArray(src.other)     ? src.other.filter(Boolean)     : [];
    // why: for percent fields we return `null` when the source is missing /
    // empty / unparseable so the caller's `?? 10` fallback fires. Returning
    // 0 here would short-circuit the fallback and default every AI item that
    // omits `contingency_pct` to zero contingency — financially wrong.
    const pickPct = (a, b) => {
        const raw = a ?? b;
        if (raw == null) return null;
        if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
        const s = String(raw).trim();
        if (s === '') return null;
        // parseFloat on the cleaned numeric form detects "NaN" / "abc" etc.
        // as not-a-real-number BEFORE parseAmount clamps them to 0. This lets
        // us distinguish a legitimate "0" / "0%" (returns 0) from a broken
        // "NaN" / "" / "tbd" (returns null, so caller's `?? 10` fires).
        const cleaned = s.replace(/[$,%\s]/g, '');
        if (cleaned === '' || !Number.isFinite(parseFloat(cleaned))) return null;
        return parseAmount(raw);
    };
    return {
        directCostTotal: parseAmount(src.direct_cost_total ?? src.directCostTotal),
        contingencyPct:  pickPct(src.contingency_pct, src.contingencyPct),
        overheadPct:     pickPct(src.overhead_pct, src.overheadPct),
        marginPct:       pickPct(src.margin_pct, src.marginPct),
        materials: mats.map((m) => ({
            description: String(m?.description || ''),
            qty: parseAmount(m?.qty),
            unit: String(m?.unit || ''),
            unitRate: parseAmount(m?.unitRate ?? m?.unit_rate),
            total: parseAmount(m?.total),
        })),
        labour: lab.map((l) => ({
            trade: String(l?.trade || ''),
            hours: parseAmount(l?.hours),
            rate: parseAmount(l?.rate),
            // why: workers defaults to 1 when absent — legacy schema
            // behaviour preserved. Empty / null string → 1, not 0.
            workers: (l?.workers == null || l?.workers === '') ? 1 : parseAmount(l?.workers),
            total: parseAmount(l?.total),
        })),
        equipment: eq.map((e) => ({
            description: String(e?.description || ''),
            total: parseAmount(e?.total),
        })),
        subcontractors: sub.map((s) => ({
            trade: String(s?.trade || s?.description || s?.name || ''),
            hours: parseAmount(s?.hours),
            qty: (s?.qty == null || s?.qty === '') ? null : parseAmount(s?.qty),
            unit: String(s?.unit || ''),
            rate: parseAmount(s?.rate ?? s?.unitRate ?? s?.unit_rate ?? s?.cost),
            total: parseAmount(s?.total),
        })),
        other: oth.map((o) => ({
            description: String(o?.description || ''),
            total: parseAmount(o?.total),
        })),
    };
}

// why: breakdown rows are not uniform. Materials use `total`, labour uses
// rate*hours*workers, equipment uses total|cost. Consumer already denormalised
// these into `total` in most paths, so summing `total` is safe + cheap.
function sumRowTotals(rows = []) {
    if (!Array.isArray(rows)) return 0;
    let s = 0;
    for (const r of rows) {
        if (!r) continue;
        const t = parseAmount(r.total ?? r.amount ?? 0);
        if (Number.isFinite(t)) s += t;
    }
    return s;
}

function sumLabourCost(rows = []) {
    if (!Array.isArray(rows)) return 0;
    let s = 0;
    for (const r of rows) {
        if (!r) continue;
        // Prefer pre-computed `total`; fall back to rate × hours × workers
        if (r.total != null) {
            const t = parseAmount(r.total);
            if (Number.isFinite(t)) { s += t; continue; }
        }
        const rate = parseAmount(r.rate);
        const hrs = parseAmount(r.hours || 1);
        const workers = parseAmount(r.workers || 1);
        s += rate * hrs * workers;
    }
    return s;
}

function pickMarginPct(...values) {
    for (const value of values) {
        if (value == null || value === '') continue;
        if (typeof value === 'number') {
            if (Number.isFinite(value)) return value;
            continue;
        }
        const text = String(value).trim();
        if (!text) continue;
        const cleaned = text.replace(/[$,%\s]/g, '');
        if (cleaned === '' || !Number.isFinite(parseFloat(cleaned))) continue;
        return parseAmount(value);
    }
    return undefined;
}

/**
 * Map a scope-item array to cost-math's LineItemInputs[].
 *
 * Runtime scope rows are authored by Stage 3/WMS and default to client-facing
 * inc-GST amounts. Callers can still pass `isGstInclusive:false` for the
 * active ex-GST display mode; that flag is now an explicit view concern, not
 * legacy project inference.
 *
 * @param {Array} scopeItems
 * @param {Object} [opts]
 * @param {boolean} [opts.isGstInclusive=true]  `item.amount` is inc-GST unless a caller explicitly supplies ex-GST display rows
 * @param {number}  [opts.defaultMarginPct]      project margin when an item has no override
 * @param {boolean} [opts.useVisibleAmountOverride=false] honour finite item.amount for every line
 * @returns {Array}
 */
export function mapScopeToInputs(scopeItems = [], opts = {}) {
    if (!Array.isArray(scopeItems)) return [];
    const isGstInclusive = opts.isGstInclusive !== false; // default TRUE to match Stage 3/WMS runtime scope
    // Slice 0 (DEC-PE-008): rate-book-matched lines pass through at their final
    // inc-GST price. Caller may force via opts.rateFinalNoMarkup; otherwise read
    // the per-device flag (default off). Hoisted so the localStorage read is once-per-call.
    const rateFinalNoMarkup = opts.rateFinalNoMarkup ?? isRateFinalNoMarkupEnabled();
    return scopeItems.map((item) => {
        const cb = item?.costBreakdown || {};
        const materialsCost   = sumRowTotals(cb.materials);
        const labourCost      = sumLabourCost(cb.labour);
        const equipmentCost   = sumRowTotals(cb.equipment);
        const subcontractCost = sumRowTotals(cb.subcontractors);
        let otherCost         = sumRowTotals(cb.otherCosts || cb.other);
        const explicitDirectCost = parseAmount(cb.directCostTotal);
        const rowDirectCost = materialsCost + labourCost + equipmentCost + subcontractCost + otherCost;
        if (explicitDirectCost > 0) {
            if (rowDirectCost <= 0.01) {
                otherCost = explicitDirectCost;
            } else if (explicitDirectCost > rowDirectCost && Math.abs(explicitDirectCost - rowDirectCost) > 1) {
                otherCost += explicitDirectCost - rowDirectCost;
            }
        }

        const amountLocked = item?.amountLocked === true || item?.amount_locked === true;
        const methodologyCommitted = item?.amountAuthority === 'committed'
            && typeof cb?.pricingMethodologyId === 'string'
            && cb.pricingMethodologyId.length > 0;
        // Scope morph may retain the deterministic floor-rounded amount while
        // unresolved pricing evidence keeps the item reviewable. Stage 4 must
        // project that same live amount; re-deriving from partial cost rows can
        // show a lower total than both the scope state and the pricing floor.
        const provisionalReviewAmount = item?.amountAuthority === 'provisional-builder-review'
            && item?.pricingReviewRequired === true;
        // An item priced from its own lines (quote-brain-buildup.ts) keeps that
        // price, as a locked one does: an old cost breakdown it still carries
        // never re-derives it (Peter, 2026-11-02).
        const pricedFromLines = Array.isArray(item?.quoteBrain?.buildUp) && item.quoteBrain.buildUp.length > 0;
        const pricing = resolveScopePricing(item, isGstInclusive);
        const preserveCostAmounts = cb.diagnosticCostAdjusted === true
            || cb.costSource === 'diagnostic-service-guard'
            || cb.costSource === 'statutory-admin';
        // DATA-1: convert to cost-math's ex-GST canonical when the caller says
        // the visible amount is inc-GST. Explicit ex-GST display rows pass
        // `isGstInclusive:false`.
        let amountOverride;
        let amountOverrideIncGst;
        if (amountLocked || methodologyCommitted || provisionalReviewAmount || pricedFromLines || opts.useVisibleAmountOverride) {
            const raw = parseAmount(item?.amount);
            if (pricing.isGstInclusive) {
                const inc = pricing.quotedAmountIncGst ?? raw;
                if (inc > 0) amountOverrideIncGst = inc;
            } else {
                const ex = pricing.quotedAmountExGst ?? raw;
                if (ex > 0) amountOverride = ex;
            }
        }

        const title = String(item?.title || '');
        // Harden: catch the full statutory-insurance spellings, not just "hbcf"
        // (shared name regex with the committed engine + the INV5 invariant).
        const hbcfApplicable = STATUTORY_INSURANCE_RE.test(title)
            || STATUTORY_INSURANCE_RE.test(String(item?.id || ''))
            || managedScopeItemIncludesHbcf(item);
        const excludeFromHbcfEligibility = isHbcfEligibilityExcludedItem(item);

        // Slice 0 (rate-final-no-markup, DEC-PE-008): when enabled, rate-book-
        // matched lines (rateIsFinalIncGst) are Rodney's FINAL inc-GST price and
        // must not be marked up. Lump the to-mark-up remainder into a single
        // cost component (the markup stack applies only to it) and pass the
        // matched total to cost-math as `finalIncGstAddon`. Locked / cost-
        // preserved items are left alone. Default off → bit-identical.
        let mMaterials = materialsCost;
        let mLabour = labourCost;
        let mEquip = equipmentCost;
        let mSub = subcontractCost;
        let mOther = otherCost;
        let finalIncGstAddon;
        let finalExGstAddon;
        let contingencyOverride = preserveCostAmounts ? parseAmount(cb.contingencyAmount) : undefined;
        let overheadOverride = preserveCostAmounts ? parseAmount(cb.overheadAmount) : undefined;
        let excludeFixed = item?.excludeFromFixedAllocations === true;
        if (rateFinalNoMarkup && !amountLocked && !preserveCostAmounts) {
            const part = partitionCostLines(cb);
            if (part.hasFinalLines) {
                finalIncGstAddon = part.finalIncGstTotal;
                finalExGstAddon = part.finalExGstTotal;
                mMaterials = 0;
                mLabour = 0;
                mEquip = 0;
                mSub = 0;
                mOther = part.markupDirectTotal;
                if (part.markupDirectTotal <= 0.01) {
                    // Pure-final item: nothing to mark up → zero contingency +
                    // overhead and keep it out of the fixed-overhead denominator.
                    contingencyOverride = 0;
                    overheadOverride = 0;
                    excludeFixed = true;
                }
            }
        }

        return {
            id: item?.id,
            title,
            materialsCost: mMaterials,
            labourCost: mLabour,
            equipmentCost: mEquip,
            subcontractCost: mSub,
            otherCost: mOther,
            contingencyPct: cb.contingencyPct,
            overheadPct: cb.overheadPct,
            contingencyAmountOverride: contingencyOverride,
            overheadAmountOverride: overheadOverride,
            marginPct: pickMarginPct(
                item?.marginPercent,
                item?.marginPctOverride,
                cb.targetMarginPct,
                opts.defaultMarginPct,
                cb.marginPct,
            ),
            amountOverride,
            amountOverrideIncGst,
            amountLocked,
            provisionalSum: !!item?.provisionalSum,
            hbcfApplicable,
            excludeFromHbcfEligibility,
            finalIncGstAddon,
            finalExGstAddon,
            // DATA-2: statutory insurance (HBCF) carries no GST. Scope items
            // never set this flag directly: cost-rules.applyHbcf builds the
            // HBCF line with `gstExempt:true`. Preserve if already present.
            gstExempt: !!item?.gstExempt,
            excludeFromFixedAllocations: excludeFixed,
        };
    });
}

/**
 * Build a ProjectCostConfig from a business costingConfig. The Costing Rules
 * (overhead, contingency, markup brackets) were retired with build-up pricing
 * step 2 (Peter, 2026-11-03): values a business stored are not read, so no
 * cost gains an overhead or contingency share here. An old item's own stored
 * contingency or overhead percentage still applies to it.
 * @param {Object} costingConfig
 * @returns {Object}
 */
export function buildCostConfig(costingConfig = {}) {
    const configuredHbcf = costingConfig?.hbcf || {};
    const thresholdInput = configuredHbcf.thresholdIncGst
        ?? configuredHbcf.threshold_inc_gst
        ?? costingConfig?.pricingMethodology?.hbcf?.thresholdIncGst
        ?? costingConfig?.pricingMethodology?.hbcf?.threshold_inc_gst
        ?? costingConfig?.pricing_methodology?.hbcf?.thresholdIncGst
        ?? costingConfig?.pricing_methodology?.hbcf?.threshold_inc_gst
        ?? HBCF_THRESHOLD_INCGST;
    const thresholdNumber = Number(String(thresholdInput).replace(/[$,\s]/g, ''));
    const thresholdIncGst = Number.isFinite(thresholdNumber) && thresholdNumber >= 0
        ? thresholdNumber
        : HBCF_THRESHOLD_INCGST;

    return {
        contingencyPct: 0,
        overheadPct: 0,
        overhead: { mode: 'percent' },
        gstRate: 0.10,
        // why: canonical form is ex-GST: display toggle is a UI concern
        gstInclusive: false,
        roundItemIncGst: costingConfig?.roundItemIncGst !== false,
        itemRoundUnit: parseAmount(costingConfig?.itemRoundUnit ?? 10) || 10,
        hbcf: {
            ...configuredHbcf,
            rate: Number.isFinite(Number(configuredHbcf.rate))
                ? Number(configuredHbcf.rate)
                : HBCF_DEFAULT_RATE,
            thresholdIncGst,
        },
    };
}

/**
 * Diff-log helper. Fires `[CostMath diff]` when legacy vs cost-math disagree
 * by more than $0.01 AND the `QM_LOG_COST_MATH_DIFF` window flag is set.
 * Never throws; safe to call from render paths.
 */
export function logCostMathDiff(surface, legacy, cmTotal, extra = {}) {
    if (typeof window === 'undefined') return;
    if (!window.QM_LOG_COST_MATH_DIFF) return;
    const delta = Math.round((cmTotal - legacy) * 100) / 100;
    if (Math.abs(delta) <= 0.01) return;
    // eslint-disable-next-line no-console
    console.info('[CostMath diff]', { surface, legacy, cost_math: cmTotal, delta, ...extra });
}

/**
 * Cost-math v2 is the sole production pricing path.
 *
 * The legacy per-browser cutover switches were retired in Checkpoint 13.
 * Pricing authority must not vary by browser origin or a stale localStorage
 * value: the same persisted project must calculate identically everywhere.
 *
 * @returns {true}
 */
export function isCostMathV2Enabled() {
    return true;
}

/**
 * Canonical rate-final-no-markup contract (Slice 0, DEC-PE-008).
 * Rate-book-matched lines
 * (`rateIsFinalIncGst`) pass through at their final inc-GST price with NO
 * contingency / overhead / margin / GST re-applied — Rodney's catalogue
 * charge-out rate reaches the client unchanged. COUPLED with
 * catalogue-first costing: the steer makes the AI pick
 * catalogue rates by exact name, this makes them pass through without markup.
 * They are two halves of one behaviour — enabling only one is wrong (steer-only
 * marks the real rate up; no-markup-only has nothing matched to pass through).
 * The historical per-device opt-out was retired in v5.65.18 so the same
 * persisted Project cannot price differently in another browser. The helper
 * is re-exported for existing import sites and mapScopeToInputs.
 * @returns {boolean}
 */
export { isRateFinalNoMarkupEnabled };

/** Legacy compatibility export. The canonical cost-math path cannot be disabled. */
export function isCostMathDisabled() {
    return false;
}
